import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { setUpdateDrmTableData, setResetUpdateDrmTableDataState } from "../redux/reducers/drmUpdateTableDataSlice";
import { setDeleteDrmTableData, setResetDeleteDrmTableDataState } from "../redux/reducers/drmtDeleteTableDataSlice";
import { setSubmitDrmTableData, setResetSubmitDrmTableDataState } from "../redux/reducers/drmtSubmitTableDataSlice";
import { setExpireDrmTableData, setResetExpireDrmTableDataState } from "../redux/reducers/drmExpireTableDataSlice";
import { useTranslation } from "react-i18next";

const REQUIRED_ACCESSORS = [
  "carrierScac",
  "serviceScac",
  "tariffId",
  "tariffNumber",
  "orgHub",
  "originZone",
  "originCountry",
  "equipmentType",
  "destHub",
  "destinationZone",
  "destinationCountry",
  "effectiveDate",
  "expirationDate",
  "modeOfTransport",
  "lineHaulRate"
];

const NUMBER_ACCESSORS = [
  "lineHaulRate",
  "rate",
  "pctAward",
  "maxCarrierAllocationPercentage",
  "maxShipmentsAllocated",
];

const FLEXIBLE_ACCESSOR_MAP = {
  orgHub: ["destinationZone", "destHub"],
  originZone: ["destinationZone", "destHub"],
  destinationZone: ["orgHub", "originZone"],
  destHub: ["orgHub", "originZone"],
};

const isEmptyValue = (value) => value === undefined || value === null || value === "" || value === "-";

const hasMissingRequiredFields = (records = []) =>
  records.some((record = {}) =>
    REQUIRED_ACCESSORS.some((accessor) => {
      const value = record?.[accessor];

      if (!isEmptyValue(value)) {
        return false;
      }

      const flexibleFallbacks = FLEXIBLE_ACCESSOR_MAP[accessor];

      if (!flexibleFallbacks || flexibleFallbacks.length === 0) {
        return true;
      }

      const hasFallbackValue = flexibleFallbacks.some((fallbackAccessor) => {
        const fallbackValue = record?.[fallbackAccessor];
        return !isEmptyValue(fallbackValue);
      });

      return !hasFallbackValue;
    })
  );

const getMissingColumn = (records = []) => {
  for (const record of records) {
    for (const accessor of REQUIRED_ACCESSORS) {
      const value = record?.[accessor];

      if (!isEmptyValue(value)) continue;

      const flexibleFallbacks = FLEXIBLE_ACCESSOR_MAP[accessor];

      if (!flexibleFallbacks || flexibleFallbacks.length === 0) {
        return accessor;
      }

      const hasFallbackValue = flexibleFallbacks.some(
        (fallbackAccessor) => !isEmptyValue(record?.[fallbackAccessor])
      );

      if (!hasFallbackValue) return accessor;
    }
  }
  return "";
};

const hasInvalidLineHaulRate = (records = []) =>
  records.some((record = {}) => {
    const rate = Number(record?.lineHaulRate);
    return !rate || rate <= 0;
  });

const validateNumberFields = (records = []) =>
  records.some((record = {}) =>
    NUMBER_ACCESSORS.some((accessor) => {
      const value = record?.[accessor];

      if (value === undefined || value === null || value === "" || value === "-") {
        return false;
      }
      const numericValue = Number(value);
      return Number.isNaN(numericValue) || numericValue <= 0;
    })
  );

export const useBidTableOperations = (dispatch, drmTableData) => {
  const [apiSnackbar, setApiSnackbar] = useState({
    message: "",
    severity: "",
    open: false,
  });

  const {
    errorMessage: updateErrorMsg = "",
    successMessage: updateSuccessMsg = "",
    isLoading: updateIsLoading = false,
  } = useSelector((state) => state?.drmUpdates || {});
  const { userInfo } = useSelector((state) => state.user);
  const { t } = useTranslation();

  const {
    errorMessage: deleteErrorMsg = "",
    successMessage: deleteSuccessMsg = "",
    isLoading: deleteIsLoading = false,
  } = useSelector((state) => state?.drmDelete || {});

  const {
    errorMessage: submitErrorMsg = "",
    successMessage: submitSuccessMsg = "",
    isLoading: submitIsLoading = false,
  } = useSelector((state) => state?.drmSubmit || {});

  const {
    errorMessage: expireErrorMsg = "",
    successMessage: expireSuccessMsg = "",
    isLoading: expireIsLoading = false,
  } = useSelector((state) => state?.drmExpire || {});

  const triggerRequiredFieldsWarning = useCallback((missingColumn) => {
    setApiSnackbar({
      message: `${t("daily_required_fields_missing")} ${t(missingColumn)}`,
      severity: "warning",
      open: true,
    });
  }, []);

  const triggerLineHaulRateWarning = useCallback(() => {
    setApiSnackbar({
      message: t("line_haul_rate_must_be_greater_than_zero"),
      severity: "warning",
      open: true,
    });
  }, []);

  const triggerValidationWarning = useCallback(() => {
    setApiSnackbar({
      message: t("must_be_greater_than_zero"),
      severity: "warning",
      open: true,
    });
  }, []);

  const handleSaveConfirm = useCallback(() => {
    const controller = new AbortController();
    const modifiedRowIds = Object.keys(drmTableData?.modifiedRows || {});
    const selectedRows = drmTableData?.selectedRows || [];
    const selectedUpdatedRows = selectedRows.filter((row) =>
      modifiedRowIds.includes(row.original.sagaId?.toString())
    );
    const updatedTableData = selectedUpdatedRows.map((row) =>
      drmTableData?.drmMaintenanceDetails?.[row.index]
    ).filter(Boolean);

    if (hasMissingRequiredFields(updatedTableData)) {
      const missingColumn = getMissingColumn(updatedTableData);
      triggerRequiredFieldsWarning(missingColumn);
      return () => {
        controller.abort();
      };
    }

    if (hasInvalidLineHaulRate(updatedTableData)) {
      triggerLineHaulRateWarning();
      return () => {
        controller.abort();
      };
    }

    if (validateNumberFields(updatedTableData)) {
      triggerValidationWarning();
      return () => {
        controller.abort();
      };
    }

    dispatch(
      setUpdateDrmTableData({
        userCode: userInfo?.userName,
        updatedTableData,
        tableName: "drmBidTableData",
        controller,
      })
    );

    return () => {
      controller.abort();
    };
  }, [drmTableData, userInfo, triggerRequiredFieldsWarning]);

  const handleSubmitConfirm = useCallback(() => {
    const selectedRows = drmTableData?.selectedRows || [];
    const isUpdateExist = selectedRows.some((row) => {
      const modifiedRowIds = Object.keys(drmTableData?.drmUpdatedMaintenanceDetails || {});
      return modifiedRowIds.includes(row.original?.sagaId?.toString());
    });

    const isReadyToProcessStatus = selectedRows.every((row) => row?.original?.status === 0);

    if (!isReadyToProcessStatus) {
      setApiSnackbar({
        message: t("submit_status_validation"),
        severity: "warning",
        open: true,
      });
      return;
    }
    if (isUpdateExist) {
      setApiSnackbar({
        message: t("drmt_bid_update_validation"),
        severity: "warning",
        open: true,
      });
      return;
    }

    if (isUpdateExist) {
      setApiSnackbar({
        message: t("drmt_bid_update_validation"),
        severity: "warning",
        open: true,
      });
      return;
    }
    const selectedBidDetails = selectedRows.filter(
      (row) => drmTableData?.drmMaintenanceDetails?.[row.index]
    );
    const selectedRecordData = selectedBidDetails
      .map((row) => drmTableData?.drmMaintenanceDetails?.[row.index])
      .filter(Boolean);

    if (hasMissingRequiredFields(selectedRecordData)) {
      triggerRequiredFieldsWarning();
      return;
    }

    const submitDetails = selectedBidDetails.map((n) => ({
      sagaId: n?.original?.sagaId,
      version: n?.original?.versionNumber,
    }));
    const controller = new AbortController();

    dispatch(
      setSubmitDrmTableData({
        userCode: userInfo?.userName,
        submitDetails,
        tableName: "drmBidTableData",
        controller,
      })
    );

    return () => {
      controller.abort();
    };
  }, [drmTableData, userInfo, triggerRequiredFieldsWarning]);

  const handleDeleteConfirm = useCallback(() => {
    const selectedRows = drmTableData?.selectedRows || [];
    const selectedBidDetails = selectedRows.filter(
      (row) => drmTableData?.drmMaintenanceDetails?.[row.index]
    );
    const deleteData = selectedBidDetails.map((n) => ({
      sagaId: n?.original?.sagaId,
      version: n?.original?.versionNumber,
    }));
    const controller = new AbortController();

    dispatch(
      setDeleteDrmTableData({
        deleteData,
        tableName: "drmBidTableData",
        controller,
      })
    );

    return () => {
      controller.abort();
    };
  }, [drmTableData]);

  const handleExpireConfirm = useCallback(() => {
    const selectedRows = drmTableData?.selectedRows || [];
    const selectedBidDetails = selectedRows.filter(
      (row) => drmTableData?.drmMaintenanceDetails?.[row.index]
    );
    const expireData = selectedBidDetails.map((n) => ({
      sagaId: n?.original?.sagaId,
      version: n?.original?.versionNumber,
    }));
    const controller = new AbortController();

    dispatch(
      setExpireDrmTableData({
        userCode: userInfo?.userID,
        submitDetails: expireData,
        tableName: "drmBidTableData",
        controller,
      })
    );
    return () => {
      controller.abort();
    };
  }, [drmTableData, userInfo, triggerRequiredFieldsWarning]);


  useEffect(() => {
    if (!updateIsLoading && updateSuccessMsg) {
      setApiSnackbar({
        message: updateSuccessMsg,
        severity: "info",
        open: true,
      });
      dispatch(setResetUpdateDrmTableDataState());
    } else if (!updateIsLoading && updateErrorMsg) {
      setApiSnackbar({
        message: updateErrorMsg,
        severity: "error",
        open: true,
      });
      dispatch(setResetUpdateDrmTableDataState());
    }
  }, [updateIsLoading, updateSuccessMsg, updateErrorMsg]);

  const formatMessageAsList = (msg) => {
    if (!msg?.includes("\n")) return msg;
    const lines = msg.split("\n").filter(Boolean);
    return (
      <div style={{ margin: 0, paddingLeft: 0 }}>
        {lines.map((line) => (
          <div key={line}>{line}</div>
        ))}
      </div>
    );
  };

  useEffect(() => {
    if (!deleteIsLoading && deleteSuccessMsg) {
      setApiSnackbar({
        message: formatMessageAsList(deleteSuccessMsg),
        severity: "info",
        open: true,
      });
      dispatch(setResetDeleteDrmTableDataState());
    } else if (!deleteIsLoading && deleteErrorMsg) {
      setApiSnackbar({
        message: deleteErrorMsg,
        severity: "error",
        open: true,
      });
      dispatch(setResetDeleteDrmTableDataState());
    }
  }, [deleteIsLoading, deleteSuccessMsg, deleteErrorMsg]);

  useEffect(() => {
    if (!submitIsLoading && submitSuccessMsg) {
      setApiSnackbar({
        message: submitSuccessMsg,
        severity: "info",
        open: true,
      });
      dispatch(setResetSubmitDrmTableDataState());
    } else if (!submitIsLoading && submitErrorMsg) {
      setApiSnackbar({
        message: submitErrorMsg,
        severity: "error",
        open: true,
      });
      dispatch(setResetSubmitDrmTableDataState());
    }
  }, [submitIsLoading, submitSuccessMsg, submitErrorMsg]);

  useEffect(() => {
    if (!expireIsLoading && expireSuccessMsg) {
      setApiSnackbar({
        message: expireSuccessMsg,
        severity: "info",
        open: true,
      });
      dispatch(setResetExpireDrmTableDataState());
    } else if (!expireIsLoading && expireErrorMsg) {
      setApiSnackbar({
        message: expireErrorMsg,
        severity: "error",
        open: true,
      });
      dispatch(setResetExpireDrmTableDataState());
    }
  }, [expireIsLoading, expireSuccessMsg, expireErrorMsg]);

  const apiSnackClose = () => {
    setApiSnackbar({ message: "", severity: "", open: false });
  };

  return {
    apiSnackbar,
    apiSnackClose,
    handleSaveConfirm,
    handleSubmitConfirm,
    handleDeleteConfirm,
    handleExpireConfirm,
  };
};
